/**
 * Shared form validation utilities used by both the public form viewer
 * and the builder's PreviewModal so validation behaviour is identical.
 */

// Regex patterns that correspond to the preset names from ValidationRulesPanel.
export const PRESET_PATTERNS = {
  email:        /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/,
  url:          /^(https?:\/\/)[^\s/$.?#].[^\s]*$/,
  numeric:      /^[0-9]+$/,
  alpha:        /^[a-zA-Z\s]+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
};

/**
 * Run all configured validation rules for a field and return the first error
 * message, or null if the value is valid.
 *
 * Handles: required (with custom message), pattern (field-level regex),
 * and all explicit validationRules (minLength, maxLength, minValue, maxValue,
 * pattern, email, url, numeric, alpha, alphanumeric).
 *
 * Cross-field dependency errors are intentionally excluded — callers that need
 * dependency-driven errors (setError actions) should handle them separately.
 *
 * @param {object} field   - The field/element config object.
 * @param {*}      value   - The current field value (string, array, or undefined).
 * @returns {string|null}
 */
export const validateField = (field, value) => {
  const isEmpty =
    value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

  // Required check (custom message support)
  if (field?.required && isEmpty) {
    return field?.requiredMessage || 'This field is required';
  }

  // Skip further checks if the field is empty and not required
  if (isEmpty) return null;

  const strVal = Array.isArray(value) ? value.join(',') : String(value ?? '');

  // Pattern matching (stored directly on the field — Pattern Matching section)
  if (field?.pattern) {
    try {
      if (!new RegExp(field.pattern).test(strVal)) {
        return field?.patternMessage || 'Please enter a valid value';
      }
    } catch {
      // Invalid regex stored in the builder — skip silently
    }
  }

  // Explicit validation rules from the Validation Rules section
  for (const rule of (field?.validationRules || [])) {
    let failed = false;
    const ruleVal = rule?.value;

    switch (rule?.type) {
      case 'minLength':
        failed = strVal.length < parseInt(ruleVal, 10);
        break;
      case 'maxLength':
        failed = strVal.length > parseInt(ruleVal, 10);
        break;
      case 'minValue':
        failed = parseFloat(strVal) < parseFloat(ruleVal);
        break;
      case 'maxValue':
        failed = parseFloat(strVal) > parseFloat(ruleVal);
        break;
      case 'pattern':
        try { failed = !new RegExp(ruleVal).test(strVal); } catch { failed = false; }
        break;
      case 'email':
        failed = !PRESET_PATTERNS.email.test(strVal);
        break;
      case 'url':
        failed = !PRESET_PATTERNS.url.test(strVal);
        break;
      case 'numeric':
        failed = !PRESET_PATTERNS.numeric.test(strVal);
        break;
      case 'alpha':
        failed = !PRESET_PATTERNS.alpha.test(strVal);
        break;
      case 'alphanumeric':
        failed = !PRESET_PATTERNS.alphanumeric.test(strVal);
        break;
      default:
        break;
    }

    if (failed) {
      return rule?.message || `Validation failed: ${rule?.type}`;
    }
  }

  return null;
};
